import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Switch,
    FormControlLabel,
    Divider,
    Alert,
    CircularProgress
} from '@mui/material';
import { HiOutlineArrowLeft, HiSave } from 'react-icons/hi';
import { useNavigate } from 'react-router-dom';
import authService from '../../services/authService';
import oeeService from '../../services/oeeService';
import './OeeSettings.css';

const OeeSettings = () => {
    const navigate = useNavigate();
    const [settings, setSettings] = useState({
        enableStartupLoss: true,
        enableChangeoverLoss: true,
        enableSpeedLoss: true,
        enableQualityLoss: true,
        includeShiftBreak: true
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchSettings();
    }, []);

    const fetchSettings = async () => {
        setLoading(true);
        try {
            const tenantId = authService.getTenantId();
            const data = await oeeService.getSettings(tenantId);
            if (data) {
                setSettings({
                    enableStartupLoss: data.enableStartupLoss,
                    enableChangeoverLoss: data.enableChangeoverLoss,
                    enableSpeedLoss: data.enableSpeedLoss,
                    enableQualityLoss: data.enableQualityLoss,
                    includeShiftBreak: data.includeShiftBreak
                });
            }
        } catch (err) {
            console.error("Failed to fetch OEE settings", err);
            setError("Could not load settings. Using defaults.");
        } finally {
            setLoading(false);
        }
    };

    const handleToggle = (key) => {
        setSettings(prev => ({ ...prev, [key]: !prev[key] }));
        setMessage(null); // Clear previous success msg on change
    };

    const handleSave = async () => {
        setSaving(true);
        setError(null);
        setMessage(null);
        try {
            const tenantId = authService.getTenantId();
            await oeeService.updateSettings(tenantId, settings);
            setMessage("Settings saved successfully.");
        } catch (err) {
            console.error("Failed to save OEE settings", err);
            setError("Failed to save settings. Please try again.");
        } finally {
            setSaving(false);
        }
    };

    if (loading) return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
            <CircularProgress />
        </Box>
    );

    return (
        <div className="min-h-screen bg-page-bg/50 p-0 font-sans text-primary">

            <div className="page-header">
                <div className="header-content">
                    <div className='d-flex'>
                        <button
                            onClick={() => navigate(-1)}
                            className="oee-report-back-btn mr-2"
                        >
                            <HiOutlineArrowLeft size={18} />
                        </button>
                        <h1>OEE Configuration</h1>
                    </div>

                </div>

            </div>

            {/* Header */}
            {/* <div className="flex items-center gap-4 mb-8 bg-card p-4 rounded-xl shadow-sm border border-gray-100">
                <button 
                    onClick={() => navigate(-1)} 
                    className="p-2 hover:bg-gray-100 rounded-full transition-colors text-tertiary"
                >
                    <HiArrowLeft size={24} />
                </button>
                <h1 className="text-2xl font-bold text-primary tracking-tight">OEE Configuration</h1>
            </div> */}

            <div className="w-full configurationpage">
                {message && <Alert severity="success" sx={{ mb: 3 }}>{message}</Alert>}
                {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

                <Card sx={{ borderRadius: 2, boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)' }}>
                    <CardContent className="p-6">
                        <div className="flex justify-between items-center mb-4">
                            <Typography variant="h6" fontWeight="bold" color="text.primary">
                                Calculation Rules
                            </Typography>
                            <button
                                className="btn-primary flex items-center gap-2"
                                onClick={handleSave}
                                disabled={saving}
                            >
                                {saving ? <CircularProgress size={20} color="inherit" /> : <HiSave size={20} />}
                                {saving ? 'Saving...' : 'Save Changes'}
                            </button>
                        </div>

                        <Divider sx={{ mb: 4 }} />

                        <div className="flex flex-col gap-6">
                            <SettingItem
                                label="Rule 1: Startup Loss"
                                description="Automatically log 'Starup Loss' if the first production run starts later than the shift start time."
                                checked={settings.enableStartupLoss}
                                onChange={() => handleToggle('enableStartupLoss')}
                            />

                            <SettingItem
                                label="Rule 2: Changeover Loss"
                                description="Automatically log 'Changeover Loss' for the time gap between consecutive production runs of different products."
                                checked={settings.enableChangeoverLoss}
                                onChange={() => handleToggle('enableChangeoverLoss')}
                            />

                            <SettingItem
                                label="Rule 3: Performance Loss"
                                description="Automatically calculate and log 'Performance Loss' at the end of a run. Formula: Run Duration − (Cycle Time × Units Produced) − Unplanned Downtime."
                                checked={settings.enableSpeedLoss}
                                onChange={() => handleToggle('enableSpeedLoss')}
                            />

                            <SettingItem
                                label="Rule 4: Reject/Rework Loss"
                                description="Automatically create a loss log corresponding to the time lost due to Rejects and Reworks."
                                checked={settings.enableQualityLoss}
                                onChange={() => handleToggle('enableQualityLoss')}
                            />

                            <Divider />

                            <SettingItem
                                label="Rule 5: Include Shift Break"
                                description="Include Shift Break duration in OEE 'Planned Production Time'. If disabled, break time is subtracted from OEE availability calculations."
                                checked={settings.includeShiftBreak}
                                onChange={() => handleToggle('includeShiftBreak')}
                            />
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};

const SettingItem = ({ label, description, checked, onChange }) => (
    <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
            <Typography variant="subtitle1" fontWeight="600" color="text.primary">
                {label}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: '90%' }}>
                {description}
            </Typography>
        </div>
        <Switch
            checked={checked}
            onChange={onChange}
            color="primary"
        />
    </div>
);

export default OeeSettings;
